import { useAuthStore } from '@/stores/auth'
import type { Claim } from '@/types/claim'
import type { ContractRequest, ContractRequestArchived } from '@/types/contractRequests'
import type { Contract } from '@/types/contracts'
import type {
  DeleteDocumentData,
  Document,
  DocumentCreated,
  DocumentData,
  DrivingLicenceDocumentData,
  SupportingDocument,
  UserDocument
} from '@/types/documents'
import type {
  DocumentTicketData,
  DocumentTicketSubmitted,
  LifeProcedure,
  LifeProcedureData,
  LifeProcedureUpdated,
  TicketCreated,
  TicketData
} from '@/types/general'
import type {
  ApiToken,
  Authenticated,
  Company,
  CompanyMember,
  Me,
  MeToken,
  NotificationCount,
  Profile,
  ProfileType
} from '@/types/me'
import type { AllNotifications } from '@/types/notifications'
import type { PremiumDueNotice } from '@/types/premiumDueNotice'
import { CookiesName, getCookie } from '@/utils/cookies'
import axios from 'axios'
import { AxiosError } from 'axios'

const SECURITY_TOKEN = import.meta.env.VITE_SECURITY_TOKEN
const BACKEND_URL = import.meta.env.VITE_RAILS_BACKEND_URL
const backendUrl = (path: string) => `${BACKEND_URL}/api/my/v1${path}`

const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api/my/v1`
})
apiClient.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  if (authStore.authToken) {
    config.headers['X-Auth-Token'] = authStore.authToken
  }
  if (localStorage.getItem('authToken')) {
    config.headers['X-Auth-Token'] = localStorage.getItem('authToken')
  }
  if (getCookie(CookiesName.Token)) {
    config.headers['X-Auth-Token'] = getCookie(CookiesName.Token)
  }
  return config
})

const getMe = async () =>
  await apiClient.request<{ data: Me }>({
    method: 'GET',
    url: backendUrl('/me'),
    withCredentials: true
  })

const login = async (email: string, password: string) => {
  return await apiClient.request<ApiToken>({
    method: 'post',
    url: backendUrl('/auth/login'),
    data: {
      email: email,
      password: password
    },
    withCredentials: true
  })
}

const getCompanyDetails = async (companyId: string) =>
  await apiClient.request<{ data: Company }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}`),
    withCredentials: true
  })

const getCompanyMembers = async (companyId: string) =>
  await apiClient.request<{ data: CompanyMember[] }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}/members`),
    withCredentials: true
  })

const getMeToken = async () =>
  await apiClient.request<{ data: MeToken }>({
    method: 'GET',
    url: backendUrl('/me/token'),
    withCredentials: true
  })

export const getUserProfiles = async () =>
  await apiClient.request<{ data: Profile[] }>({
    method: 'GET',
    url: backendUrl(`/me/profiles`)
  })

export const getNotificationCount = async () =>
  await apiClient.request<{ data: NotificationCount }>({
    method: 'GET',
    url: backendUrl(`/notifications/count`),
    withCredentials: true
  })

export const getNotifications = async () =>
  await apiClient.request<{ data: AllNotifications }>({
    method: 'GET',
    url: backendUrl(`/notifications`),
    withCredentials: true
  })

const postOnboard = async (type: ProfileType) =>
  await apiClient.request<{ data: 'onboarded' }>({
    method: 'POST',
    url: backendUrl('/onboard'),
    data: { onboarding_type: type },
    withCredentials: true
  })

const getContractRequests = async () =>
  await apiClient.request<{ data: ContractRequest[] }>({
    method: 'GET',
    url: backendUrl('/contract_requests'),
    withCredentials: true
  })

const getContracts = async () =>
  await apiClient.request<{ data: Contract[] }>({
    method: 'GET',
    url: backendUrl('/contracts'),
    withCredentials: true
  })

const getCompanyContracts = async (companyId: string) =>
  await apiClient.request<{ data: Contract[] }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}/contracts`),
    withCredentials: true
  })

const getCompanyContract = async (companyId: string, contractId: string) =>
  await apiClient.request<{ data: Contract }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}/contracts/${contractId}`),
    withCredentials: true
  })

const getDocuments = async () =>
  await apiClient.request<{ data: UserDocument[] }>({
    method: 'GET',
    url: backendUrl('/documents?type=upload'),
    withCredentials: true
  })

const getInsuranceCertificates = async () =>
  await apiClient.request<{ data: Contract[] }>({
    method: 'GET',
    url: `${BACKEND_URL}/api/my/v1/insurance_certificates`,
    withCredentials: true
  })

const getB2BInsuranceCertificates = async (companyId: string) =>
  await apiClient.request<{ data: Contract[] }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}/insurance_certificates`),
    withCredentials: true
  })

const getDocumentsToSign = async () =>
  await apiClient.request<{ data: Document[] }>({
    method: 'GET',
    url: backendUrl('/documents?type=sign'),
    withCredentials: true
  })

const getCompanyDocuments = async (companyId: string) =>
  await apiClient.request<{ data: Document[] }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}/documents`),
    withCredentials: true
  })

const getPremiumDueNotices = async (contractNumber: string) =>
  await apiClient.request<{ data: PremiumDueNotice[] }>({
    method: 'GET',
    url: backendUrl(`/contracts/${contractNumber}/premium_due_notices`),
    withCredentials: true
  })

export const visitPage = async (pageURL: String) =>
  await apiClient.request({
    method: 'POST',
    url: backendUrl(`/visit`),
    data: {
      url: pageURL
    },
    withCredentials: true
  })

export const getClaim = async (uuid: String) =>
  await apiClient.request<{ data: Claim }>({
    method: 'GET',
    url: backendUrl(`/claims/${uuid}`),
    withCredentials: true
  })

export const getClaims = async () =>
  await apiClient.request<{ data: Claim[] }>({
    method: 'GET',
    url: backendUrl(`/claims`),
    withCredentials: true
  })

export const getCompanyClaims = async (companyId: string) =>
  await apiClient.request<{ data: Claim[] }>({
    method: 'GET',
    url: backendUrl(`/companies/${companyId}/claims`),
    withCredentials: true
  })

export const getContract = async (uuid: String) =>
  await apiClient.request<{ data: Contract }>({
    method: 'GET',
    url: backendUrl(`/contracts/${uuid}`),
    withCredentials: true
  })

export const getContractInvoiceUrl = async (uuid: string) =>
  await apiClient.request<{ data: string }>({
    method: 'GET',
    url: backendUrl(`/contracts/${uuid}/invoice_url`),
    withCredentials: true
  })

export const getContractSupportingDocuments = async (reference: string) =>
  await apiClient.request<{ data: SupportingDocument[] }>({
    method: 'GET',
    url: backendUrl(`/contracts/${reference}/supporting_documents`),
    withCredentials: true
  })

export const getContractRequest = async (uuid: String) =>
  await apiClient.request<{ data: ContractRequest }>({
    method: 'GET',
    url: backendUrl(`/contract_requests/${uuid}`),
    withCredentials: true
  })

export const getLifeProcedure = async (uuid: String) =>
  await apiClient.request<{ data: LifeProcedure }>({
    method: 'GET',
    url: backendUrl(`/life_procedures/${uuid}`),
    withCredentials: true
  })

export const patchLifeProcedure = async (body: LifeProcedureData): Promise<LifeProcedureUpdated> => {
  const { data } = await apiClient.request<LifeProcedureUpdated>({
    method: 'PATCH',
    url: backendUrl(`/life_procedures/${body.lifeProcedureUuid}`),
    data: { life_procedure: { payload: body.payload } },
    withCredentials: true
  })

  return data
}

export const changePassword = async (currentPassword: string, newPassword: string) => {
  const { data } = await apiClient.request<{ message: string }>({
    method: 'PATCH',
    url: backendUrl('/me/password'),
    data: {
      user: {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPassword
      }
    },
    withCredentials: true
  })

  return !!data.message
}

export const updateMobilePhone = async (mobilePhone: string): Promise<boolean> => {
  const { data } = await apiClient.request<{ message: string }>({
    method: 'PUT',
    url: backendUrl('/me/phone'),
    data: { mobile_phone: mobilePhone },
    withCredentials: true
  })

  return !!data.message
}

export const updateEmail = async (email: string): Promise<boolean> => {
  const { data } = await apiClient.request<{ message: string }>({
    method: 'PUT',
    url: backendUrl('/me/email'),
    data: { email },
    withCredentials: true
  })

  return !!data.message
}

export const resetPassword = async (email: string, password: string, reset_password_token: string): Promise<any> => {
  return apiClient.request<{ message: string }>({
    method: 'POST',
    url: backendUrl('/auth/reset_password'),
    data: {
      password: password,
      email: email,
      reset_token: reset_password_token
    }
  })
}

export const sendResetPasswordLink = async (email: string): Promise<any> => {
  const { data } = await apiClient.request<{ message: string }>({
    method: 'POST',
    url: backendUrl('/auth/generate_password_reset_token'),
    data: {
      email: email
    }
  })

  return data
}

export const postTicket = async (body: TicketData): Promise<boolean> => {
  const { data } = await apiClient.request<TicketCreated>({
    method: 'POST',
    url: backendUrl(`/tickets`),
    data: body,
    withCredentials: true
  })
  return data.ticket_created
}

export const submitDocumentTicket = async (body: DocumentTicketData): Promise<string> => {
  const { data } = await apiClient.request<DocumentTicketSubmitted>({
    method: 'POST',
    url: backendUrl(`/document_tickets`),
    data: body,
    withCredentials: true
  })
  return data.message
}

export const archiveContractRequest = async (contractUuid: string): Promise<boolean> => {
  const { data } = await apiClient.request<ContractRequestArchived>({
    method: 'POST',
    url: backendUrl(`/contract_requests/${contractUuid}/archive`),
    withCredentials: true
  })

  return data.contract_request_archived
}

export const postDocument = async (body: DocumentData): Promise<any> => {
  const formData = new FormData()
  for (const [key, value] of Object.entries(body)) {
    formData.append(key, value)
  }
  const { data } = await axios.request<DocumentCreated>({
    method: 'POST',
    url: backendUrl(`/contract_requests/${body.contractRequestUuid}/documents`),
    data: formData,
    headers: {
      'x-security-token': SECURITY_TOKEN,
      'X-Auth-Token': localStorage.getItem('authToken')
    }
  })

  return [data]
}

export const deleteDocument = async (body: DeleteDocumentData): Promise<any> => {
  const { data } = await axios.request<DocumentCreated>({
    method: 'DELETE',
    url: backendUrl(`/documents/${body.documentUuid}/files/${body.fileId}`),
    headers: {
      'X-Auth-Token': localStorage.getItem('authToken')
    },
    withCredentials: true
  })

  return [data]
}

export const patchDrivingLicence = async (body: DrivingLicenceDocumentData): Promise<any> => {
  const { data } = await axios.request<DocumentCreated>({
    method: 'PATCH',
    url: backendUrl(`/driving_licences/${body.uuid}`),
    data: { temporary: body.temporay, person_uuid: body.personUuid },
    headers: {
      'X-Auth-Token': localStorage.getItem('authToken')
    },
    withCredentials: true
  })
  return data
}

export const patchMotoLicencePlate = async (
  licencePlate: string,
  motorbikeBelongingUuid: string,
  contractUuid: string
): Promise<any> => {
  const { data } = await apiClient.request<{}>({
    method: 'PATCH',
    url: backendUrl(`/belongings/${motorbikeBelongingUuid}`),
    data: { belonging: { licence_plate: licencePlate, contract_uuid: contractUuid } },
    withCredentials: true
  })
  return data
}

const authenticated = async (): Promise<boolean> => {
  const { data } = await apiClient.request<Authenticated>({
    method: 'GET',
    url: backendUrl('/auth/authenticated')
  })
  return data.authenticated
}

//* ERROR HANDLING

const isUnauthorizedError = (error: unknown): boolean => error instanceof AxiosError && error.response?.status === 401

const AxiosErrorApiResponse = AxiosError<{ errors: string[] }>

const errorToMessages = (error: unknown): string[] => {
  // TODO: i18n
  if (error instanceof AxiosErrorApiResponse) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // Extract the error message from the response
      try {
        const { errors } = JSON.parse(error.response.data)
        return errors
      } catch (e) {
        return ['Something wrong happened. Please contact us for assistance.']
      }
    } else if (error.request) {
      // The request was made but no response was received
      return ['No response received']
    } else {
      // Something happened in setting up the request that triggered an error
      return [`Error: ${error.message}`]
    }
  } else {
    // Something happened in setting up the request that triggered an error
    return [`Error: ${(error as Error).message}`]
  }
}

export default {
  authenticated,
  getMe,
  getMeToken,
  getContractRequests,
  getContracts,
  getB2BInsuranceCertificates,
  getContract,
  getContractInvoiceUrl,
  getClaim,
  getClaims,
  getContractRequest,
  getLifeProcedure,
  getDocuments,
  getInsuranceCertificates,
  getDocumentsToSign,
  getPremiumDueNotices,
  changePassword,
  updateMobilePhone,
  updateEmail,
  login,
  resetPassword,
  sendResetPasswordLink,
  postTicket,
  postDocument,
  deleteDocument,
  isUnauthorizedError,
  errorToMessages,
  visitPage,
  postOnboard,
  getUserProfiles,
  getCompanyContracts,
  getCompanyContract,
  getCompanyClaims,
  getCompanyDetails,
  getCompanyMembers,
  getCompanyDocuments,
  getContractSupportingDocuments,
  getNotificationCount,
  getNotifications,
  apiClient
}
